import { apiFetch } from '..'

/**
 * タグ型
 */
export interface OpportunityTag {
  id: number
  tag_id: number
  name: string
  group?: string
}

/**
 * タグ一覧レスポンス
 */
export interface GetTagsResponse {
  tags: OpportunityTag[]
}

/**
 * タグ更新レスポンス
 */
export interface UpdateTagsResponse {
  tags: OpportunityTag[]
}

/**
 * 全タグ一覧レスポンス（オートコンプリート用）
 */
export interface GetAllTagsResponse {
  total: number
  tags: {
    id: number
    name: string
    group: string | null
    isReadonly: boolean
    updatedAt?: string
    createdAt?: string
  }[]
}

/**
 * タグ一覧取得API
 *
 * @param opportunityId - オポチュニティID
 * @returns タグ一覧レスポンス
 * @throws ApiError - 認証失敗（403）、権限不足（403）、サーバーエラー（500）
 */
export async function getOpportunityTags(opportunityId: number): Promise<GetTagsResponse> {
  return apiFetch(`/api/opportunity/${opportunityId}/tags`, { method: 'GET' })
}

/**
 * タグ更新API
 *
 * @param opportunityId - オポチュニティID
 * @param tags - タグ配列（{ name: string } の配列）
 * @returns 更新されたタグ一覧
 * @throws ApiError - 認証失敗（403）、権限不足（403）、サーバーエラー（500）
 */
export async function updateOpportunityTags(
  opportunityId: number,
  tags: { name: string }[],
): Promise<UpdateTagsResponse> {
  return apiFetch(`/api/opportunity/${opportunityId}/tags`, {
    method: 'PUT',
    body: JSON.stringify({ tags }),
  })
}

type TagsQueryParams = {
  tagonly?: boolean
  offset?: number
  limit?: number
}

/** URLSearchParams の構築を担当 */
function buildTagsQueryString(params?: TagsQueryParams): string {
  const queryParams = new URLSearchParams()
  if (params?.tagonly) {
    queryParams.append('tagonly', 'true')
  }
  if (params?.offset !== undefined) {
    queryParams.append('offset', params.offset.toString())
  }
  if (params?.limit !== undefined) {
    queryParams.append('limit', params.limit.toString())
  }
  return queryParams.toString()
}

/** タグ名から `group:name` 形式をパースし group フィールドを付与 */
function parseTagGroup(tag: GetAllTagsResponse['tags'][number]): GetAllTagsResponse['tags'][number] {
  if (tag.name.match(/^[a-z]+:/)) {
    const [group] = tag.name.split(/:/, 2)
    return { ...tag, group: group ?? null }
  }
  return { ...tag, group: null }
}

/** グループ名で降順ソート */
function sortTagsByGroup(tags: GetAllTagsResponse['tags']): GetAllTagsResponse['tags'] {
  return [...tags]
    .sort((a, b) => {
      const groupA = a.group ?? ''
      const groupB = b.group ?? ''
      if (groupA < groupB) return -1
      if (groupA > groupB) return 1
      return 0
    })
    .reverse()
}

/**
 * 全タグ一覧取得API（オートコンプリート用）
 * 参照: 旧環境 GET /api/v1/tags
 *
 * @param params - クエリパラメータ
 * @param params.tagonly - true の場合、updatedAt, createdAt を除外
 * @param params.offset - オフセット（デフォルト: 0）
 * @param params.limit - 上限（オプション）
 * @returns タグ一覧レスポンス
 * @throws ApiError - 認証失敗（403）、サーバーエラー（500）
 */
export async function getAllTags(params?: TagsQueryParams): Promise<GetAllTagsResponse> {
  const queryString = buildTagsQueryString(params)
  const url = `/api/tags${queryString ? `?${queryString}` : ''}`

  const response = await apiFetch<GetAllTagsResponse>(url, { method: 'GET' })

  const parsedTags = response.tags.map(parseTagGroup)
  const sortedTags = sortTagsByGroup(parsedTags)

  return { ...response, tags: sortedTags }
}
